import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowPathIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

// Content Snapshot imports
import { useContentSnapshotStore, useActiveContentInfo } from '../../store/contentSnapshotStore';
import { ProfileContentCard } from '../../components/profiles/ProfileContentCard';

// Fallback imports
import type { ProfileData } from '../../data/profileMappings';
import { getAllProfiles } from '../../data/profileMappings';

export const ProfilesViewer: React.FC = () => {
  const [useMockData, setUseMockData] = useState(false);
  
  // Content Snapshot store
  const {
    profiles,
    activeContentInfo,
    isLoading,
    isRefreshing,
    error,
    hasAccess,
    lastFetch,
    cacheInfo,
    fetchProfiles,
    refreshProfiles,
    clearError,
    checkAccess,
  } = useContentSnapshotStore();

  // Fallback data
  const mockProfiles = getAllProfiles();

  // Check access and load data on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        await checkAccess();
        if (hasAccess !== false) {
          await fetchProfiles();
        }
      } catch (error) {
        console.error('Erro na inicialização:', error);
        setUseMockData(true);
      }
    };

    initializeData();
  }, []);

  // Auto-fallback se não tem acesso ou erro
  useEffect(() => {
    if (hasAccess === false || (error && !isLoading)) {
      setUseMockData(true);
    }
  }, [hasAccess, error, isLoading]);

  const handleRefresh = async () => {
    try {
      clearError();
      await refreshProfiles();
      if (profiles.length > 0) {
        setUseMockData(false);
      }
    } catch (error) {
      console.error('Erro no refresh:', error);
    }
  };

  const handleToggleDataSource = () => {
    setUseMockData(!useMockData);
    if (!useMockData && hasAccess !== false) {
      clearError();
    }
  };

  // Generate mock assessment results for fallback display
  const generateMockAssessmentResults = (profile: ProfileData) => ({
    primary_profile: profile.name,
    confidence_score: 85,
    secondary_profile: profile.id === 'assertiva' ? 'Liderança Inspiradora' : undefined,
    detailed_analysis: {
      opportunities: profile.growthOpportunities,
      leadership_style: profile.leadershipStyle,
    },
    communication_style: {
      dominant_traits: [profile.communicationStyle],
    },
  });

  // Convert mock profiles to ProfileContentCard format
  const convertMockProfile = (profile: ProfileData) => ({
    id: profile.id,
    nome: profile.name,
    descricao_resumida: profile.description,
    descricao_completa: `${profile.description} Este perfil demonstra características de ${profile.communicationStyle.toLowerCase()} e tende a liderar através de ${profile.leadershipStyle.toLowerCase()}.`,
    pontos_fortes: profile.strengths.map(strength => ({
      titulo: strength,
      descricao: `Demonstra competência em ${strength.toLowerCase()} de forma consistente.`,
      exemplo_pratico: `Exemplo prático de aplicação de ${strength.toLowerCase()} em situações empresariais.`,
    })),
    areas_desenvolvimento: profile.growthOpportunities.map(opportunity => ({
      titulo: opportunity,
      descricao: `Oportunidade de crescimento em ${opportunity.toLowerCase()}.`,
      impacto: `Desenvolver ${opportunity.toLowerCase()} pode ter impacto significativo na eficácia profissional.`,
      sugestoes: [
        `Praticar ${opportunity.toLowerCase()} em situações de baixo risco`,
        `Buscar feedback sobre ${opportunity.toLowerCase()}`,
        `Estudar casos de sucesso relacionados a ${opportunity.toLowerCase()}`,
      ],
    })),
    plano_acao: {
      '30_dias': [
        {
          tarefa: `Autoavaliação de ${profile.name}`,
          descricao: `Realizar uma autoavaliação detalhada do perfil ${profile.name}`,
          area_melhoria: 'Autoconhecimento',
          complexidade: 'baixa',
        }
      ],
      '90_dias': [
        {
          tarefa: `Desenvolvimento focado em ${profile.strengths[0] || 'comunicação'}`,
          descricao: `Focar no desenvolvimento e aprimoramento desta característica`,
          area_melhoria: profile.strengths[0] || 'Comunicação',
          complexidade: 'media',
        }
      ],
      '6_meses': [
        {
          tarefa: `Integração completa do perfil ${profile.name}`,
          descricao: `Aplicar consistentemente as características do perfil no ambiente profissional`,
          area_melhoria: 'Aplicação Prática',
          complexidade: 'alta',
        }
      ],
    },
    estrategias_especificas: {
      networking: [`Estratégia de networking para perfil ${profile.name}`],
      apresentacoes: [`Estratégia de apresentação para perfil ${profile.name}`],
      negociacoes: [`Estratégia de negociação para perfil ${profile.name}`],
      lideranca: [`Estratégia de liderança para perfil ${profile.name}`],
    },
  });

  // Determine which data to show
  const displayProfiles = useMockData 
    ? mockProfiles.map(convertMockProfile)
    : profiles;

  const dataSourceInfo = useMockData 
    ? 'Dados mock (desenvolvimento)' 
    : activeContentInfo 
      ? `Versão ativa: ${activeContentInfo.version} (ID: ${activeContentInfo.active_version.id.slice(0, 8)}...)`
      : 'Content Snapshot';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Title */}
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900">
              Perfis de Comunicação
            </h1>
            <p className="text-gray-600 mt-1">
              Visualização detalhada dos perfis do sistema de gerenciamento de conteúdo
            </p>
          </div>

          {/* Status and Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Data Source Info */}
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                useMockData 
                  ? 'bg-amber-100 text-amber-800' 
                  : hasAccess 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
              }`}>
                {useMockData ? (
                  <ExclamationTriangleIcon className="w-4 h-4" />
                ) : hasAccess ? (
                  <ShieldCheckIcon className="w-4 h-4" />
                ) : (
                  <ShieldExclamationIcon className="w-4 h-4" />
                )}
                <span>{dataSourceInfo}</span>
              </div>

              {lastFetch && !useMockData && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <ClockIcon className="w-3 h-3" />
                  Atualizado: {new Date(lastFetch).toLocaleTimeString()}
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {/* Toggle Data Source */}
              <button
                onClick={handleToggleDataSource}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {useMockData ? 'Usar Dados Reais' : 'Usar Mock Data'}
              </button>

              {/* Refresh Button */}
              {!useMockData && (
                <button
                  onClick={handleRefresh}
                  disabled={isLoading || isRefreshing}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Atualizando...' : 'Atualizar'}
                </button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && !useMockData && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800">Erro ao carregar dados</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                  <p className="text-sm text-red-600 mt-2">
                    Os dados mock estão sendo exibidos como fallback.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Access Info */}
          {hasAccess === false && !useMockData && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-blue-800">Acesso Administrativo Necessário</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Para visualizar dados reais do content snapshot, faça login com uma conta administrativa.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && !useMockData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-pulse">
                <div className="bg-gray-200 h-32"></div>
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Profiles Grid */}
        {!isLoading && displayProfiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {displayProfiles.map((profile, index) => (
              <motion.div
                key={profile.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: "easeOut",
                  delay: index * 0.1 
                }}
              >
                <ProfileContentCard profile={profile} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty State */}
        {!isLoading && displayProfiles.length === 0 && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum perfil encontrado</h3>
              <p className="text-gray-600 mb-4">
                Não foi possível carregar os perfis no momento.
              </p>
              <button
                onClick={handleRefresh}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Tentar Novamente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Debug Info (só em desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && cacheInfo && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white text-xs p-3 rounded-lg max-w-xs">
          <div className="font-medium mb-1">Debug Info</div>
          <div>Cache entries: {cacheInfo.totalCacheEntries}</div>
          <div>Active cached: {cacheInfo.activeInfoCached ? 'Yes' : 'No'}</div>
          <div>Profiles: {displayProfiles.length}</div>
          <div>Source: {useMockData ? 'Mock' : 'API'}</div>
        </div>
      )}
    </div>
  );
};